import { useId, useState } from 'react';
import { RadioGroup } from 'radix-ui';
import { Check, Pipette } from 'lucide-react';
import type { ProjectColor } from '@timato/shared';
import { cn } from '@/lib/cn';
import { COLOR_NAME, PRESET_COLORS, colorVar, isPreset, onProjectColor } from '@/lib/projectColors';

/** Colour picker (8j): ten named colours, plus anything else through the browser's own picker. */
type Props<N extends boolean> = {
  value: N extends true ? ProjectColor | null : ProjectColor;
  onChange: (c: N extends true ? ProjectColor | null : ProjectColor) => void;
  allowNone?: N;
  label?: string;
};

const DEFAULT_CUSTOM = '#7C5CE0';
const swatch = 'w-8 h-8 rounded-full border-2 cursor-pointer grid place-items-center transition-transform hover:scale-105';

export function ColorSwatches<N extends boolean = false>({ value, onChange, allowNone, label = 'Colour' }: Props<N>) {
  const id = useId();
  const custom = value && !isPreset(value) ? value : null;
  // Remembered so the custom swatch keeps its colour while a named one is selected.
  const [recent, setRecent] = useState(custom ?? DEFAULT_CUSTOM);
  const shown = custom ?? recent;

  const pickCustom = (hex: string) => { setRecent(hex as ProjectColor); onChange(hex as never); };

  return (
    <RadioGroup.Root value={value ?? 'none'} onValueChange={(v) => onChange((v === 'none' ? null : v) as never)} aria-label={label} className="flex flex-wrap items-center gap-3">
      {allowNone && (
        <RadioGroup.Item value="none" aria-label="No tint" className={cn(swatch, 'bg-card', value == null ? 'border-ink' : 'border-chip-border')}>
          <RadioGroup.Indicator><Check size={14} strokeWidth={3} className="text-ink" /></RadioGroup.Indicator>
        </RadioGroup.Item>
      )}
      {PRESET_COLORS.map((c) => (
        <RadioGroup.Item key={c} value={c} aria-label={COLOR_NAME[c]}
          className={cn(swatch, value === c ? 'border-ink' : 'border-transparent')} style={{ background: colorVar(c) }}>
          <RadioGroup.Indicator><Check size={14} strokeWidth={3} style={{ color: onProjectColor(c) }} /></RadioGroup.Indicator>
        </RadioGroup.Item>
      ))}

      {/* The native picker: every colour, no dependency, and the platform's own eyedropper on desktop. */}
      <span className="relative inline-flex">
        <label htmlFor={id} aria-label="Custom colour"
          className={cn(swatch, 'relative', custom ? 'border-ink' : 'border-chip-border border-dashed')} style={{ background: custom ? shown : 'transparent' }}>
          {custom
            ? <Check size={14} strokeWidth={3} style={{ color: onProjectColor(custom) }} />
            : <Pipette size={14} className="text-ink-2" />}
        </label>
        <input id={id} type="color" value={shown} onChange={(e) => pickCustom(e.target.value)}
          className="absolute inset-0 w-full h-full opacity-0 cursor-pointer" />
      </span>
    </RadioGroup.Root>
  );
}
